import type { ResponseOf } from '@tuyau/core/types'
import { format } from 'date-fns'
import type { routes } from 'gozoki-backend/registry'
import type { PermissionTuple } from '~/utils/ability'

/**
 * Lecture du suivi projet. Partagé par la route `/project-tracking` et par le post-login (`routes/`),
 * qui doit tester cette permission sans importer la page.
 */
export const PROJECT_TRACKING_READ: PermissionTuple = ['read', 'project-tracking.board.ticket']

/**
 * Les lectures Notion sont servies derrière un cache de 2 à 3 min ; passé cette marge, `syncedAt`
 * ne bouge plus que si Notion répond, donc une valeur plus vieille est forcément servie en grace
 * period (M9).
 */
const STALE_AFTER_MS = 15 * 60 * 1000

/** `checkedAt` = dernier fetch réussi : c'est lui qui fait ressortir un `syncedAt` figé. */
export const staleLabel = (syncedAt: string, checkedAt: number): string | null => {
  const synced = new Date(syncedAt)
  if (checkedAt - synced.getTime() < STALE_AFTER_MS) return null

  // Date absolue, jamais de libellé relatif : la page n'affiche aucun « il y a N minutes ».
  return `Données du ${format(synced, "dd/MM 'à' HH:mm")}`
}

export type ProjectTrackingBoard = ResponseOf<(typeof routes)['project_tracking.board']>

/** La fiche (`GET tickets/:id`) : propriétés + fil de commentaires **live** fail-closed. */
export type ProjectTrackingSheet = ResponseOf<(typeof routes)['project_tracking.show_ticket']>

export type ProjectTrackingSheetTicket = ProjectTrackingSheet['ticket']

export type ProjectTrackingComment = ProjectTrackingSheet['comments'][number]

/**
 * Les deux cartes du panneau « Synthèses ». `day` et `weekly` ne sont **pas la même ligne** :
 * l'hebdo est calendaire, seule la première ligne d'une semaine en porte une.
 */
export type ProjectTrackingDashboard = ResponseOf<(typeof routes)['project_tracking.dashboard']>

export type ProjectTrackingRoadmap = ResponseOf<(typeof routes)['project_tracking.roadmap']>

/** L'historique de la modale, paginé par un curseur **opaque** Notion (jamais un offset). */
export type ProjectTrackingSyntheses = ResponseOf<(typeof routes)['project_tracking.syntheses']>

export type ProjectTrackingSynthese = ProjectTrackingSyntheses['syntheses'][number]
